import React, { FC } from 'react';
import { CardCollapse } from 'common/components';
import { IconBackground } from 'common/components/IconBackground';
import { Divider } from 'common/components/Divider';
import { EColor } from 'common/const/color.enum';
import { AuthHooks } from 'common/hooks';
import {
  countFormatter,
  dateFormatter,
  fileSizeFormatter,
  phoneFormatter,
  priceFormatter,
} from 'common/helpers/formatter.helper';
import { useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as UserIcon } from 'app/assets/images/redesign/user.svg';
import { ReactComponent as AffiliateIcon } from 'app/assets/images/redesign/affiliate.svg';
import { ReactComponent as DeliveryIcon } from 'app/assets/images/redesign/delivery.svg';
import { ReactComponent as FileIcon } from 'app/assets/images/redesign/file.svg';
import { getRequestDetailsGeneralInfo, mapRequestMembers } from 'entities/Request/Request.helper';
import { IRequest, IRequestMembers } from 'entities/Request/Request.models';
import { EUserRole } from 'entities/User/User.enum';
import { IFile } from 'entities/File/File.models';
import { EDeliveryType } from 'entities/Basket/Basket.models';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  request: IRequest;
  members: IRequestMembers | null;
  downloadFile: (file: IFile) => void;
}

export const RequestDetailsGeneralTab: FC<IComponentProps> = ({ request, members, downloadFile }) => {
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const { isSeller } = AuthHooks.useIsSeller();
  const { count, amount, amountWithTaxes, amountVat } = getRequestDetailsGeneralInfo(request, currentUser?.account, isSeller);
  const mappedRequestMembers = mapRequestMembers(members);
  const isDelivery = request.deliveryType === EDeliveryType.Delivery;
  const isPickUp = request.deliveryType === EDeliveryType.PickUp;

  return (
    <div className="request-general">
      <div className="request-general__block">
        <div className="request-general__block-row">
          <span className="text-controls color-dark-grey">Товаров в заказе</span>

          <span className="text-controls">{countFormatter(count)}</span>
        </div>

        <div className="request-general__block-row">
          <span className="text-controls color-dark-grey">Стоимость товаров</span>

          <span className="text-controls">{priceFormatter(amount)}</span>
        </div>

        {isDelivery && (
          <div className="request-general__block-row">
            <span className="text-controls color-dark-grey">Стоимость доставки</span>

            <span className="text-controls">{priceFormatter(request.deliveryPrice)}</span>
          </div>
        )}

        <div className="request-general__block-row">
          <span className="text-controls color-dark-grey">Сумма НДС</span>

          <span className="text-controls">{priceFormatter(amountVat)}</span>
        </div>

        <div className="request-general__block-row">
          <span className="text-controls color-dark-grey">Итого с НДС</span>

          <span className="text-controls">{priceFormatter(amountWithTaxes)}</span>
        </div>
      </div>

      <Divider marginY={20} />

      <CardCollapse
        keys={['deliveryDetails']}
        items={[
          {
            key: 'deliveryDetails',
            label: <span className="text-h4">Детали доставки</span>,
            children: (
              <div className="request-general__block">
                {request?.legal && (
                  <div className="request-general__block-row">
                    <span className="text-controls color-dark-grey">Закупщик (юр. лицо)</span>

                    <span className="text-controls">{request.legal.name}</span>
                  </div>
                )}

                <div className="request-general__block-row">
                  <span className="text-controls color-dark-grey">ИНН</span>

                  <span className="text-controls">{request.legal.inn.length ? request.legal.inn : 'Не указан'}</span>
                </div>

                <div className="request-general__block-row">
                  <span className="text-controls color-dark-grey">КПП</span>

                  <span className="text-controls">{request.legal.kpp?.length ? request.legal.kpp : 'Не указан'}</span>
                </div>

                {isDelivery && (
                  <>
                    {request?.address && (
                      <div className="request-general__block-row">
                        <span className="text-controls color-dark-grey">Адрес</span>

                        <span className="text-controls">{request.address.name}</span>
                      </div>
                    )}

                    {request.deliveryDate && (
                      <div className="request-general__block-row">
                        <span className="text-controls color-dark-grey">Планируемая дата</span>

                        <span className="text-controls">{dateFormatter('DD.MM.YYYY', request.deliveryDate)}</span>
                      </div>
                    )}
                  </>
                )}

                {isPickUp && (
                  <>
                    {request.store && (
                      <div className="request-general__block-row">
                        <span className="text-controls color-dark-grey">Самовывоз</span>

                        <span className="text-controls">{request.store.address}</span>
                      </div>
                    )}

                    <div className="request-general__block-row">
                      <span className="text-controls color-dark-grey">Дата</span>

                      <span className="text-controls">
                        {request.deliveryDate ? dateFormatter('DD.MM.YYYY', request.deliveryDate) : 'Уточняется менеджером'}
                      </span>
                    </div>
                  </>
                )}

                {request.assignee && (
                  <div className="request-general__block-row">
                    <span className="text-controls color-dark-grey">Контактное лицо</span>
                    {/* TODO Fix */}
                    {/* @ts-ignore */}
                    <span className="text-controls">{request.assignee}</span>
                  </div>
                )}

                {request.phone && (
                  <div className="request-general__block-row">
                    <span className="text-controls color-dark-grey">Номер для связи</span>
                    {/* TODO Fix */}
                    {/* @ts-ignore */}
                    <span className="text-controls">{phoneFormatter(request.phone)}</span>
                  </div>
                )}

                <div className="request-general__block-row">
                  <span className="text-controls color-dark-grey">Поставщик</span>

                  <span className="text-controls">{request?.seller.name}</span>
                </div>
              </div>
            ),
          },
        ]}
      />

      {!!request.files.length && (
        <>
          <Divider marginY={20} />

          <CardCollapse
            keys={['files']}
            items={[
              {
                key: 'files',
                label: <span className="text-h4">Документы</span>,
                children: (
                  <div className="request-general__files">
                    {request.files.map((file) => {
                      return (
                        <div key={file.id} className="request-general__files-item" onClick={() => downloadFile(file)}>
                          <FileIcon />

                          <div>
                            <div className="text-controls mb-2 ellipsis" title={file.name}>
                              {file.name}
                            </div>

                            <span className="text-tag color-dark-grey">
                              {`${fileSizeFormatter(file.size)} • Загружено ${dateFormatter('DD MMMM YYYY', file.createdAt)}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}

      {mappedRequestMembers && (
        <>
          <Divider marginY={20} />

          <CardCollapse
            keys={['members']}
            items={[
              {
                key: 'members',
                label: <span className="text-h4">Лист согласования</span>,
                children: (
                  <div className="request-general__members">
                    {mappedRequestMembers.map((member, index) => {
                      return (
                        <div key={index} className="request-general__members-item">
                          {member.role === EUserRole.User && (
                            <IconBackground content={<UserIcon className="icon-user-dark-grey" />} color={EColor.LightGrey} />
                          )}
                          {member.role === EUserRole.Manager && (
                            <IconBackground
                              content={<AffiliateIcon className="icon-affiliate-dark-grey" />}
                              color={EColor.LightGrey}
                            />
                          )}
                          {member.role === EUserRole.SellerManager && (
                            <IconBackground
                              content={<DeliveryIcon className="icon-delivery-dark-grey" />}
                              color={EColor.LightGrey}
                            />
                          )}

                          <div>
                            <div className="text-controls mb-2 ellipsis" title={member.name}>
                              {member.name}
                            </div>

                            <div className="text-tag color-dark-grey mb-2">
                              {member.role === EUserRole.User && 'Заявитель'}
                              {member.role === EUserRole.Manager && 'Менеджер по закупкам'}
                              {member.role === EUserRole.SellerManager && 'Менеджер контракта'}
                            </div>

                            <div className="text-tag color-dark-grey ellipsis" title={member.contacts}>
                              {member.contacts}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};
